import type { ActivityHit, FacilityHit, PersonHit } from "@/components/AlgoliaSearch";
import { getGlobalSearchEntityRecencyPrefixes, type GlobalSearchEntityHit, type JobHit } from "./mapGlobalSearchHits";

export type { GlobalSearchEntityHit };

export type GlobalSearchProximity = {
  recentUrls?: readonly string[];
};

function recencyIndex(hit: GlobalSearchEntityHit, recentUrls: readonly string[]): number | null {
  const prefixes = getGlobalSearchEntityRecencyPrefixes(hit);
  if (prefixes.length === 0) return null;

  let best: number | null = null;
  for (const prefix of prefixes) {
    const index = recentUrls.findIndex((url) => url === prefix || url.startsWith(`${prefix}/`));
    if (index >= 0 && (best === null || index < best)) best = index;
  }
  return best;
}

/** Stable sort: viewed hits by recency, then original GraphQL order. */
export function rankGlobalSearchEntityHits<T extends GlobalSearchEntityHit>(
  hits: readonly T[],
  proximity: GlobalSearchProximity
): T[] {
  const recentUrls = proximity.recentUrls ?? [];
  if (recentUrls.length === 0 || hits.length < 2) return [...hits];

  return hits
    .map((hit, index) => ({
      hit,
      index,
      recency: recencyIndex(hit, recentUrls) ?? Number.POSITIVE_INFINITY
    }))
    .sort((a, b) => a.recency - b.recency || a.index - b.index)
    .map((entry) => entry.hit);
}

function partitionViewedHits<T extends GlobalSearchEntityHit>(
  hits: readonly T[],
  recentUrls: readonly string[]
): { viewed: T[]; rest: T[] } {
  const viewed: T[] = [];
  const rest: T[] = [];
  for (const hit of hits) {
    if (recentUrls.length > 0 && recencyIndex(hit, recentUrls) !== null) viewed.push(hit);
    else rest.push(hit);
  }
  return { viewed, rest };
}

function zipRemainder(lists: readonly (readonly GlobalSearchEntityHit[])[]): GlobalSearchEntityHit[] {
  const mixed: GlobalSearchEntityHit[] = [];
  const length = Math.max(0, ...lists.map((list) => list.length));
  for (let index = 0; index < length; index += 1) {
    for (const list of lists) {
      const hit = list[index];
      if (hit) mixed.push(hit);
    }
  }
  return mixed;
}

/**
 * All-tab merge: viewed matches first by recency across people, facilities, jobs, and activities,
 * then remaining hits interleaved one-for-one in that type order.
 */
export function interleaveGlobalSearchEntityHits(
  people: readonly PersonHit[],
  facilities: readonly FacilityHit[],
  proximity: GlobalSearchProximity,
  jobs: readonly JobHit[] = [],
  activities: readonly ActivityHit[] = []
): GlobalSearchEntityHit[] {
  const ranked: GlobalSearchEntityHit[][] = [
    rankGlobalSearchEntityHits(people, proximity),
    rankGlobalSearchEntityHits(facilities, proximity),
    rankGlobalSearchEntityHits(jobs, proximity),
    rankGlobalSearchEntityHits(activities, proximity)
  ];
  const populated = ranked.filter((list) => list.length > 0);
  if (populated.length === 0) return [];
  if (populated.length === 1) return populated[0] ?? [];

  const recentUrls = proximity.recentUrls ?? [];
  const parts = ranked.map((list) => partitionViewedHits(list, recentUrls));
  const viewed = parts
    .flatMap((part) => part.viewed)
    .sort((a, b) => {
      const aRecency = recencyIndex(a, recentUrls) ?? Number.POSITIVE_INFINITY;
      const bRecency = recencyIndex(b, recentUrls) ?? Number.POSITIVE_INFINITY;
      return aRecency - bRecency;
    });

  return [...viewed, ...zipRemainder(parts.map((part) => part.rest))];
}
